import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  GraduationCap,
  Users,
  Home,
  Phone,
  Scissors,
  ShoppingBag,
  Smartphone,
  Clock,
  BarChart3,
  QrCode,
  Wallet,
  Shield,
  Landmark,
  Stamp,
  Eye,
  ChevronDown,
  CheckCircle2,
  ArrowLeft,
  ChevronRight,
  Menu,
  X,
} from "lucide-react";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useTranslation } from "react-i18next";

/* ─────────────────────────── DATA (Translations done inline via t()) ─────────────────────────── */

/* ─────────────────────────── COMPONENTS ─────────────────────────── */

function FAQItem({ item }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#E8E4DC] rounded-2xl overflow-hidden transition-all duration-300 hover:border-[#C8A24D]/40">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-6 py-5 text-left bg-white hover:bg-[#FAFAF7] transition-colors"
      >
        <span className="text-[#0C2340] font-semibold text-base pr-4">{item.q}</span>
        <ChevronDown className={`w-5 h-5 text-[#C8A24D] flex-shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`overflow-hidden transition-all duration-300 ${open ? "max-h-96" : "max-h-0"}`}>
        <p className="px-6 pb-5 text-[#4A5568] leading-relaxed">{item.a}</p>
      </div>
    </div>
  );
}

/* ─────────────────────────── MAIN PAGE ─────────────────────────── */

export default function AffiliatesPage() {
  const { t, i18n } = useTranslation();
  const formRef = useRef(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const sectionsRef = useRef([]);

  const AUDIENCE = [
    { icon: GraduationCap, emoji: "🎓", title: t("affiliates.audience.item1.title"), text: t("affiliates.audience.item1.text") },
    { icon: Users, emoji: "🤝", title: t("affiliates.audience.item2.title"), text: t("affiliates.audience.item2.text") },
    { icon: Home, emoji: "🏠", title: t("affiliates.audience.item3.title"), text: t("affiliates.audience.item3.text") },
    { icon: Phone, emoji: "📞", title: t("affiliates.audience.item4.title"), text: t("affiliates.audience.item4.text") },
    { icon: Scissors, emoji: "💈", title: t("affiliates.audience.item5.title"), text: t("affiliates.audience.item5.text") },
    { icon: ShoppingBag, emoji: "🛒", title: t("affiliates.audience.item6.title"), text: t("affiliates.audience.item6.text") },
    { icon: Landmark, emoji: "🏛️", title: t("affiliates.audience.item7.title"), text: t("affiliates.audience.item7.text") },
    { icon: Users, emoji: "🚌", title: t("affiliates.audience.item8.title"), text: t("affiliates.audience.item8.text") },
    { icon: Smartphone, emoji: "📱", title: t("affiliates.audience.item9.title"), text: t("affiliates.audience.item9.text") },
    { icon: Phone, emoji: "🏪", title: t("affiliates.audience.item10.title"), text: t("affiliates.audience.item10.text") },
    { icon: Home, emoji: "🏢", title: t("affiliates.audience.item11.title"), text: t("affiliates.audience.item11.text") },
    { icon: Users, emoji: "☎️", title: t("affiliates.audience.item12.title"), text: t("affiliates.audience.item12.text") },
  ];

  const INCOME_TABLE = [
    { referrals: t("affiliates.income.row1"), income: "250 €" },
    { referrals: t("affiliates.income.row2"), income: "600 €" },
    { referrals: t("affiliates.income.row3"), income: "1.500 €" },
    { referrals: t("affiliates.income.row4"), income: "2.500 €" },
  ];

  const TRUST_POINTS = [
    { icon: Shield, title: t("affiliates.trustPoints.item1.title"), text: t("affiliates.trustPoints.item1.text") },
    { icon: CheckCircle2, title: t("affiliates.trustPoints.item2.title"), text: t("affiliates.trustPoints.item2.text") },
    { icon: Users, title: t("affiliates.trustPoints.item3.title"), text: t("affiliates.trustPoints.item3.text") },
  ];

  const DIFFERENTIATORS = [
    { icon: Shield, title: t("affiliates.differentiators.item1.title"), text: t("affiliates.differentiators.item1.text") },
    { icon: Wallet, title: t("affiliates.differentiators.item2.title"), text: t("affiliates.differentiators.item2.text") },
    { icon: Stamp, title: t("affiliates.differentiators.item3.title"), text: t("affiliates.differentiators.item3.text") },
    { icon: Eye, title: t("affiliates.differentiators.item4.title"), text: t("affiliates.differentiators.item4.text") },
  ];

  const PORTAL_FEATURES = [
    { icon: BarChart3, title: t("affiliates.portal.item1.title"), text: t("affiliates.portal.item1.text") },
    { icon: Clock, title: t("affiliates.portal.item2.title"), text: t("affiliates.portal.item2.text") },
    { icon: QrCode, title: t("affiliates.portal.item3.title"), text: t("affiliates.portal.item3.text") },
  ];

  const FAQ_DATA = [
    { q: t("affiliates.faq.q1"), a: t("affiliates.faq.a1") },
    { q: t("affiliates.faq.q2"), a: t("affiliates.faq.a2") },
    { q: t("affiliates.faq.q3"), a: t("affiliates.faq.a3") },
    { q: t("affiliates.faq.q4"), a: t("affiliates.faq.a4") },
  ];

  const ENTITY_TYPES = [
    { val: "locutorio", label: t("affiliates.entityTypes.locutorio") },
    { val: "academia", label: t("affiliates.entityTypes.academia") },
    { val: "asoc_cult", label: t("affiliates.entityTypes.asoc_cult") },
    { val: "asoc_dep", label: t("affiliates.entityTypes.asoc_dep") },
    { val: "ong", label: t("affiliates.entityTypes.ong") },
    { val: "gestor_alq", label: t("affiliates.entityTypes.gestor_alq") },
    { val: "barberia", label: t("affiliates.entityTypes.barberia") },
    { val: "tienda", label: t("affiliates.entityTypes.tienda") },
    { val: "creador", label: t("affiliates.entityTypes.creador") },
    { val: "money_transfer", label: t("affiliates.entityTypes.money_transfer") },
    { val: "inmo", label: t("affiliates.entityTypes.inmo") },
    { val: "call_center", label: t("affiliates.entityTypes.call_center") },
    { val: "other", label: t("affiliates.entityTypes.other") },
  ];

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  // Section reveal observer
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add("visible");
        });
      },
      { threshold: 0.1, rootMargin: "0px 0px -50px 0px" }
    );
    const sections = document.querySelectorAll(".section-reveal");
    sections.forEach((s) => observer.observe(s));
    return () => sections.forEach((s) => observer.unobserve(s));
  }, []);

  // Form state
  const [form, setForm] = useState({ name: "", business: "", type: "", email: "", phone: "" });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    // Clear error when user starts typing
    if (errors[e.target.name]) {
      setErrors({ ...errors, [e.target.name]: null });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!form.name.trim()) newErrors.name = t("affiliates.validation.name");
    if (!form.business.trim()) newErrors.business = t("affiliates.validation.business");
    if (!form.type) newErrors.type = t("affiliates.validation.type");
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!form.email.trim()) {
      newErrors.email = t("affiliates.validation.email");
    } else if (!emailRegex.test(form.email)) {
      newErrors.email = t("affiliates.validation.email_valid");
    }

    // Phone validation: 9 digits without prefix OR starts with +/00 and has digits
    const cleanedPhone = form.phone.replace(/\s/g, "");
    const isNineDigit = /^[6789]\d{8}$/.test(cleanedPhone);
    const isInternational = /^(\+|00)\d{7,15}$/.test(cleanedPhone);

    if (!form.phone.trim()) {
      newErrors.phone = t("affiliates.validation.phone");
    } else if (!isNineDigit && !isInternational) {
      newErrors.phone = t("affiliates.validation.phone_valid");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const response = await fetch("https://n8n.theroseholding.com/webhook/aliados-residenciaya", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          source: "aliados_page",
          timestamp: new Date().toISOString()
        }),
      });
      if (response.ok) {
        // Meta Pixel conversion event
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Lead');
        }
        setSubmitted(true);
      } else {
        console.error("Webhook error:", response.statusText);
        alert(t("affiliates.form_errors.submission"));
      }
    } catch (error) {
      console.error("Submission error:", error);
      alert(t("affiliates.form_errors.connection"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      {/* ──── HEADER ──── */}
      <header className="bg-white/80 backdrop-blur-md border-b border-[#E8E4DC]/50 sticky top-0 z-50 transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 hover:opacity-90 transition-opacity">
            <div className="w-9 h-9 rounded-lg bg-[#0C2340] flex items-center justify-center shadow-sm">
              <span className="text-[#C8A24D] font-bold text-base" style={{ fontFamily: "'DM Serif Display', serif" }}>R</span>
            </div>
            <div className="flex flex-col">
              <span className="text-[#0C2340] text-lg font-semibold tracking-tight leading-none" style={{ fontFamily: "'DM Serif Display', serif" }}>
                ResidenciaYA
              </span>
              <span className="text-[10px] text-[#C8A24D] font-medium tracking-[0.2em] uppercase leading-none mt-0.5">.es</span>
            </div>
          </Link>

          {/* Anchor Navigation - 5 options */}
          <nav className="hidden lg:flex items-center gap-6">
            <button onClick={() => document.getElementById('oportunidad')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm font-medium text-[#4A5568] hover:text-[#C8A24D] transition-colors">{t("affiliates.nav.opportunity")}</button>
            <button onClick={() => document.getElementById('audiencia')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm font-medium text-[#4A5568] hover:text-[#C8A24D] transition-colors">{t("affiliates.nav.forWhom")}</button>
            <button onClick={() => document.getElementById('proceso')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm font-medium text-[#4A5568] hover:text-[#C8A24D] transition-colors">{t("affiliates.nav.process")}</button>
            <button onClick={() => document.getElementById('comisiones')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm font-medium text-[#4A5568] hover:text-[#C8A24D] transition-colors">{t("affiliates.nav.commissions")}</button>
            <button onClick={() => document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' })} className="text-sm font-medium text-[#4A5568] hover:text-[#C8A24D] transition-colors">{t("affiliates.nav.faq")}</button>
          </nav>

          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <a 
              href="https://chat.whatsapp.com/FHiPtSJg7Xs9HiWqQu69ZW?mode=gi_t" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 text-sm font-bold text-white bg-[#25D366] rounded-lg hover:bg-[#20ba5a] transition-all duration-200 shadow-lg shadow-[#25D366]/20"
            >
              <Smartphone className="w-4 h-4" />
              <span className="hidden md:inline">{t("affiliates.nav.channel")}</span>
              <span className="md:hidden">{t("affiliates.nav.channelMobile")}</span>
            </a>
            <button
              onClick={scrollToForm}
              className="bg-[#C8A24D] hover:bg-[#B8920D] text-[#0C2340] font-semibold text-sm h-10 px-5 rounded-lg shadow-md shadow-[#C8A24D]/20 transition-all duration-200 hover:shadow-lg hover:shadow-[#C8A24D]/30"
            >
              {t("affiliates.nav.cta")}
            </button>
            
            {/* Mobile Menu Button */}
            <button 
              className="lg:hidden p-2 text-[#0C2340] hover:bg-[#FAFAF7] rounded-lg transition-colors"
              onClick={() => setIsMobileMenuOpen(true)}
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <div className={`fixed inset-0 z-[60] lg:hidden transition-all duration-300 ${isMobileMenuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}>
          <div className="absolute inset-0 bg-[#0C2340]/40 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
          <div className={`absolute top-0 right-0 w-[280px] h-full bg-white shadow-2xl transition-transform duration-300 ease-out transform ${isMobileMenuOpen ? "translate-x-0" : "translate-x-full"}`}>
            <div className="flex flex-col h-full bg-[#FAFAF7]">
              {/* Mobile Header */}
              <div className="p-6 border-b border-[#E8E4DC] bg-white flex items-center justify-between">
                <span className="text-xs font-bold text-[#0C2340] uppercase tracking-widest">{t("affiliates.nav.menuTitle")}</span>
                <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 text-[#4A5568] hover:bg-[#FAFAF7] rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Mobile Lang Switcher */}
              <div className="px-6 py-4 border-b border-[#E8E4DC] bg-white">
                <p className="text-[10px] text-[#4A5568] uppercase font-bold tracking-wider mb-3 leading-none">{t("header.langLabel")}</p>
                <LanguageSwitcher />
              </div>

              {/* Mobile Links */}
              <div className="flex-1 overflow-y-auto py-6 px-6 space-y-6">
                {[
                  { id: 'oportunidad', label: t("affiliates.nav.opportunity") },
                  { id: 'audiencia', label: t("affiliates.nav.forWhom") },
                  { id: 'proceso', label: t("affiliates.nav.process") },
                  { id: 'comisiones', label: t("affiliates.nav.commissions") },
                  { id: 'faq', label: t("affiliates.nav.faq") }
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    className="w-full text-left px-4 py-3 text-[#4A5568] font-medium hover:bg-[#FAFAF7] hover:text-[#C8A24D] rounded-xl transition-all"
                  >
                    {item.label}
                  </button>
                ))}
              </div>
              <div className="p-4 border-t border-[#E8E4DC] space-y-3">
                <a 
                  href="https://chat.whatsapp.com/FHiPtSJg7Xs9HiWqQu69ZW?mode=gi_t" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-4 text-sm font-bold text-white bg-[#25D366] rounded-xl shadow-lg shadow-[#25D366]/20"
                >
                  <Smartphone className="w-4 h-4" />
                  {t("affiliates.nav.channel")}
                </a>
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    scrollToForm();
                  }}
                  className="w-full bg-[#0C2340] text-white font-bold py-4 rounded-xl shadow-lg"
                >
                  {t("affiliates.nav.cta")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main>
        {/* ══════════════════════════════════════════════════
            1. HERO
        ══════════════════════════════════════════════════ */}
        <section className="relative bg-gradient-to-br from-[#0C2340] via-[#162d4a] to-[#0A1929] overflow-hidden">
          {/* Decorative circles */}
          <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#C8A24D]/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/3" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#C8A24D]/5 rounded-full blur-3xl translate-y-1/2 -translate-x-1/3" />

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
            <div className="max-w-4xl mx-auto text-center">
              {/* Urgency badge */}
              <div className="inline-flex items-center gap-2 bg-white/10 border border-[#C8A24D]/30 rounded-full px-5 py-2 mb-8 backdrop-blur-sm">
                <Clock className="w-4 h-4 text-[#C8A24D]" />
                <span className="text-[#E8D5A0] text-sm font-medium">{t("affiliates.hero.badge")}</span>
              </div>

              <h1
                className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl text-white leading-tight mb-6"
                style={{ fontFamily: "'DM Serif Display', serif" }}
              >
                {t("affiliates.hero.titlePart1")} <span className="text-[#C8A24D]">{t("affiliates.hero.titleHighlight")}</span> {t("affiliates.hero.titlePart2")}
              </h1>

              <p className="text-white/70 text-lg sm:text-xl max-w-3xl mx-auto leading-relaxed mb-10">
                {t("affiliates.hero.subtitlePart1")} <strong className="text-white">{t("affiliates.hero.subtitleHighlight")}</strong> {t("affiliates.hero.subtitlePart2")}
              </p>

              <button
                onClick={scrollToForm}
                className="inline-flex items-center gap-2 bg-[#C8A24D] text-[#0C2340] font-bold text-base sm:text-lg px-8 py-4 rounded-2xl hover:bg-[#B8922D] transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-[#C8A24D]/25 gold-shimmer"
              >
                {t("affiliates.hero.cta")}
                <ArrowRight className="w-5 h-5" />
              </button>

              {/* Trust pills */}
              <div className="flex flex-wrap justify-center gap-4 mt-10">
                {[
                  t("footer.day1"),
                  t("bottomCta.footerInfo"),
                  t("bottomCta.disclaimer")
                ].map((text) => (
                  <span key={text} className="text-white/50 text-sm border border-white/10 rounded-full px-4 py-1.5">
                    {text}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════
            2. REGISTRO (MOVED UP)
        ══════════════════════════════════════════════════ */}
        <section ref={formRef} id="registro" className="section-reveal py-20 lg:py-24 bg-[#FAFAF7]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-[#C8A24D] text-sm font-semibold uppercase tracking-wider">{t("affiliates.register.badge")}</span>
                <h2 className="text-3xl sm:text-4xl text-[#0C2340] mt-3 mb-6" style={{ fontFamily: "'DM Serif Display', serif" }}>
                  {t("affiliates.register.title")}
                </h2>
                <p className="text-[#4A5568] text-lg mb-8 leading-relaxed">
                  {t("affiliates.register.subtitle")}
                </p>
                <div className="space-y-4">
                  {[
                    t("affiliates.register.point1"),
                    t("affiliates.register.point2"),
                    t("affiliates.register.point3")
                  ].map((text, idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      <CheckCircle2 className="w-5 h-5 text-[#C8A24D]" />
                      <span className="text-[#0C2340] font-medium">{text}</span>
                    </li>
                  ))}
                </div>
              </div>

              <div>
                {!submitted ? (
                  <form onSubmit={handleSubmit} className="bg-white rounded-3xl border border-[#E8E4DC] p-6 sm:p-8 shadow-xl shadow-[#0C2340]/5 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-[#0C2340] uppercase tracking-wider mb-1.5 ml-1">{t("affiliates.register.form.name")}</label>
                        <input
                          name="name"
                          required
                          value={form.name}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 rounded-xl border ${errors.name ? 'border-red-500' : 'border-[#E8E4DC]'} bg-[#FAFAF7] text-[#0C2340] focus:outline-none focus:ring-2 focus:ring-[#C8A24D]/50 focus:border-[#C8A24D] transition-all text-sm`}
                          placeholder={t("affiliates.register.form.placeholderName")}
                        />
                        {errors.name && <p className="text-red-500 text-xs mt-1 ml-1">{errors.name}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#0C2340] uppercase tracking-wider mb-1.5 ml-1">{t("affiliates.register.form.business")}</label>
                        <input
                          name="business"
                          required
                          value={form.business}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 rounded-xl border ${errors.business ? 'border-red-500' : 'border-[#E8E4DC]'} bg-[#FAFAF7] text-[#0C2340] focus:outline-none focus:ring-2 focus:ring-[#C8A24D]/50 focus:border-[#C8A24D] transition-all text-sm`}
                          placeholder={t("affiliates.register.form.placeholderBusiness")}
                        />
                        {errors.business && <p className="text-red-500 text-xs mt-1 ml-1">{errors.business}</p>}
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#0C2340] uppercase tracking-wider mb-1.5 ml-1">{t("affiliates.register.form.type")}</label>
                      <select
                        name="type"
                        required
                        value={form.type}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 rounded-xl border ${errors.type ? 'border-red-500' : 'border-[#E8E4DC]'} bg-[#FAFAF7] text-[#0C2340] focus:outline-none focus:ring-2 focus:ring-[#C8A24D]/50 focus:border-[#C8A24D] transition-all text-sm appearance-none`}
                      >
                        <option value="">{t("affiliates.register.form.typePlaceholder")}</option>
                        {ENTITY_TYPES.map((t) => (
                          <option key={t.val} value={t.val}>{t.label}</option>
                        ))}
                      </select>
                      {errors.type && <p className="text-red-500 text-xs mt-1 ml-1">{errors.type}</p>}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-semibold text-[#0C2340] uppercase tracking-wider mb-1.5 ml-1">{t("affiliates.register.form.email")}</label>
                        <input
                          name="email"
                          type="email"
                          required
                          value={form.email}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 rounded-xl border ${errors.email ? 'border-red-500' : 'border-[#E8E4DC]'} bg-[#FAFAF7] text-[#0C2340] focus:outline-none focus:ring-2 focus:ring-[#C8A24D]/50 focus:border-[#C8A24D] transition-all text-sm`}
                          placeholder={t("affiliates.register.form.placeholderEmail")}
                        />
                        {errors.email && <p className="text-red-500 text-xs mt-1 ml-1">{errors.email}</p>}
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-[#0C2340] uppercase tracking-wider mb-1.5 ml-1">{t("affiliates.register.form.phone")}</label>
                        <input
                          name="phone"
                          type="tel"
                          required
                          value={form.phone}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 rounded-xl border ${errors.phone ? 'border-red-500' : 'border-[#E8E4DC]'} bg-[#FAFAF7] text-[#0C2340] focus:outline-none focus:ring-2 focus:ring-[#C8A24D]/50 focus:border-[#C8A24D] transition-all text-sm`}
                          placeholder={t("affiliates.register.form.placeholderPhone")}
                        />
                        {errors.phone && <p className="text-red-500 text-xs mt-1 ml-1">{errors.phone}</p>}
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={loading}
                      className={`w-full bg-[#C8A24D] text-[#0C2340] font-bold text-base py-4 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 ${
                        loading ? "opacity-70 cursor-not-allowed" : "hover:bg-[#B8922D] hover:shadow-lg hover:shadow-[#C8A24D]/25 gold-shimmer"
                      }`}
                    >
                      {loading ? t("affiliates.register.form.sending") : t("affiliates.register.form.cta")}
                      {!loading && <ArrowRight className="w-5 h-5" />}
                    </button>
                    <p className="text-center text-[#7A7A8A] text-[10px] uppercase tracking-widest font-bold">
                      {t("affiliates.register.form.disclaimer")}
                    </p>
                  </form>
                ) : (
                  <div className="bg-white rounded-3xl border border-[#C8A24D]/30 p-10 text-center shadow-xl">
                    <div className="w-16 h-16 rounded-full bg-[#C8A24D]/10 flex items-center justify-center mx-auto mb-6">
                      <CheckCircle2 className="w-8 h-8 text-[#C8A24D]" />
                    </div>
                    <h3 className="text-2xl text-[#0C2340] mb-3" style={{ fontFamily: "'DM Serif Display', serif" }}>
                      {t("affiliates.register.successTitle")}
                    </h3>
                    <p className="text-[#4A5568]">
                      {t("affiliates.register.successBody")}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════
            3. PROBLEMA Y OPORTUNIDAD
        ══════════════════════════════════════════════════ */}
        <section id="oportunidad" className="section-reveal py-20 lg:py-28 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="text-[#C8A24D] text-sm font-semibold uppercase tracking-wider">{t("affiliates.opportunity.badge")}</span>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl text-[#0C2340] mt-3 mb-8" style={{ fontFamily: "'DM Serif Display', serif" }}>
              {t("affiliates.opportunity.title")}
            </h2>
            <div className="text-[#4A5568] text-base sm:text-lg leading-relaxed space-y-5 text-left sm:text-center mb-10">
              <p>
                {t("affiliates.opportunity.textPart1")} <strong className="text-[#0C2340]">{t("affiliates.opportunity.textHighlight")}</strong> {t("affiliates.opportunity.textPart2")}
              </p>
            </div>
            <button
               onClick={scrollToForm}
               className="inline-flex items-center gap-2 text-[#C8A24D] font-bold hover:gap-3 transition-all"
            >
              {t("affiliates.opportunity.cta")} <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════
            4. PARA QUIÉN (AUDIENCIA)
        ══════════════════════════════════════════════════ */}
        <section id="audiencia" className="section-reveal py-20 lg:py-28 bg-[#FAFAF7]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="text-[#C8A24D] text-sm font-semibold uppercase tracking-wider">{t("affiliates.audience.badge")}</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl text-[#0C2340] mt-3" style={{ fontFamily: "'DM Serif Display', serif" }}>
                {t("affiliates.audience.title")}
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {AUDIENCE.map((a) => (
                <div key={a.title} className="bg-white rounded-2xl p-6 border border-[#E8E4DC] hover:border-[#C8A24D]/40 hover:shadow-lg hover:shadow-[#C8A24D]/5 transition-all duration-300 hover:-translate-y-1 group">
                  <div className="w-12 h-12 rounded-xl bg-[#0C2340]/5 flex items-center justify-center mb-4 group-hover:bg-[#C8A24D]/10 transition-colors">
                    <a.icon className="w-6 h-6 text-[#C8A24D]" />
                  </div>
                  <h3 className="text-[#0C2340] font-semibold text-base mb-2">{a.title}</h3>
                  <p className="text-[#4A5568] text-sm leading-relaxed">{a.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════
            4. QUIÉNES SOMOS / CONFIANZA
        ══════════════════════════════════════════════════ */}
        <section className="section-reveal py-20 lg:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="text-[#C8A24D] text-sm font-semibold uppercase tracking-wider">{t("affiliates.trust.badge")}</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl text-[#0C2340] mt-3" style={{ fontFamily: "'DM Serif Display', serif" }}>
                {t("affiliates.trust.title")}
              </h2>
              <p className="text-[#4A5568] mt-4 max-w-3xl mx-auto leading-relaxed">
                {t("affiliates.trust.bodyPart1")} <strong className="text-[#0C2340]">{t("affiliates.trust.bodyHighlight")}</strong> {t("affiliates.trust.bodyPart2")}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
              {TRUST_POINTS.map((tp) => (
                <div key={tp.title} className="bg-[#FAFAF7] rounded-2xl p-6 border border-[#E8E4DC]">
                  <div className="w-10 h-10 rounded-xl bg-[#C8A24D]/10 flex items-center justify-center mb-4">
                    <tp.icon className="w-5 h-5 text-[#C8A24D]" />
                  </div>
                  <h3 className="text-[#0C2340] font-semibold mb-2">{tp.title}</h3>
                  <p className="text-[#4A5568] text-sm leading-relaxed">{tp.text}</p>
                </div>
              ))}
            </div>

            {/* Guarantee Box */}
            <div className="bg-gradient-to-br from-[#0C2340] to-[#162d4a] rounded-3xl p-8 sm:p-12 text-center">
              <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-6">
                <Shield className="w-7 h-7 text-[#C8A24D]" />
              </div>
              <h3 className="text-2xl sm:text-3xl text-white mb-4" style={{ fontFamily: "'DM Serif Display', serif" }}>
                {t("affiliates.trust.guaranteeTitle")}
              </h3>
              <p className="text-white/60 text-sm font-semibold uppercase tracking-wider mb-6">{t("affiliates.trust.guaranteeSubtitle")}</p>
              <div className="max-w-2xl mx-auto text-left space-y-3 mb-8">
                {[
                  t("affiliates.trust.guaranteePoint1"),
                  t("affiliates.trust.guaranteePoint2"),
                  t("affiliates.trust.guaranteePoint3"),
                ].map((text, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-[#C8A24D] mt-0.5 flex-shrink-0" />
                    <span className="text-white/80 text-sm">{text}</span>
                  </div>
                ))}
              </div>
              <p className="text-[#C8A24D] font-semibold text-lg">
                {t("affiliates.trust.guaranteeConclusion")}
              </p>
              <p className="text-white/50 text-sm mt-2">{t("affiliates.trust.guaranteeDisclaimer")}</p>
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════
            5. DIFERENCIADORES
        ══════════════════════════════════════════════════ */}
        <section className="section-reveal py-20 lg:py-28 bg-[#FAFAF7]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="text-[#C8A24D] text-sm font-semibold uppercase tracking-wider">{t("affiliates.differentiators.badge")}</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl text-[#0C2340] mt-3" style={{ fontFamily: "'DM Serif Display', serif" }}>
                {t("affiliates.differentiators.title")}
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {DIFFERENTIATORS.map((d) => (
                <div key={d.title} className="bg-white rounded-2xl p-6 border border-[#E8E4DC] text-center hover:shadow-lg hover:shadow-[#C8A24D]/5 transition-all duration-300 hover:-translate-y-1">
                  <div className="w-12 h-12 rounded-xl bg-[#C8A24D]/10 flex items-center justify-center mx-auto mb-4">
                    <d.icon className="w-6 h-6 text-[#C8A24D]" />
                  </div>
                  <h3 className="text-[#0C2340] font-semibold text-sm mb-2">{d.title}</h3>
                  <p className="text-[#4A5568] text-xs leading-relaxed">{d.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════
            6. PORTAL DEL ALIADO (TECNOLOGÍA)
        ══════════════════════════════════════════════════ */}
        <section className="section-reveal py-20 lg:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="text-[#C8A24D] text-sm font-semibold uppercase tracking-wider">{t("affiliates.portal.badge")}</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl text-[#0C2340] mt-3" style={{ fontFamily: "'DM Serif Display', serif" }}>
                {t("affiliates.portal.title")}
              </h2>
              <p className="text-[#4A5568] mt-4 max-w-2xl mx-auto">
                {t("affiliates.portal.subtitle")}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {PORTAL_FEATURES.map((f) => (
                <div key={f.title} className="relative bg-gradient-to-b from-[#0C2340] to-[#162d4a] rounded-2xl p-8 text-center group">
                  <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center mx-auto mb-5 group-hover:bg-[#C8A24D]/20 transition-colors">
                    <f.icon className="w-7 h-7 text-[#C8A24D]" />
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-3">{f.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{f.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════
            7. PROCESO (HOME STYLE)
        ══════════════════════════════════════════════════ */}
        <section id="proceso" className="section-reveal py-20 lg:py-28 bg-[#FAFAF7]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <span className="text-[#C8A24D] text-sm font-semibold uppercase tracking-wider">{t("affiliates.process.badge")}</span>
              <h2 className="text-3xl sm:text-4xl lg:text-[42px] text-[#0C2340] leading-tight mt-3 mb-4" style={{ fontFamily: "'DM Serif Display', serif" }}>
                {t("affiliates.process.title")}
              </h2>
              <p className="text-lg text-[#4A5568] leading-relaxed">
                {t("affiliates.process.subtitle")}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
              {[
                { step: "01", title: t("affiliates.process.step1Title"), text: t("affiliates.process.step1Desc"), icon: Wallet, detail: t("affiliates.process.step1Detail") },
                { step: "02", title: t("affiliates.process.step2Title"), text: t("affiliates.process.step2Desc"), icon: QrCode, detail: t("affiliates.process.step2Detail") },
                { step: "03", title: t("affiliates.process.step3Title"), text: t("affiliates.process.step3Desc"), icon: BarChart3, detail: t("affiliates.process.step3Detail") },
              ].map((phase, index) => {
                const Icon = phase.icon;
                return (
                  <div key={phase.step} className="group relative">
                    <div className="bg-white rounded-2xl border border-[#E8E4DC] p-8 hover:shadow-xl hover:shadow-[#0C2340]/5 transition-all duration-300 h-full relative overflow-hidden">
                      {/* Phase number background */}
                      <div className="absolute -top-4 -right-4 text-[120px] font-bold text-[#F5F1EA] leading-none select-none" style={{ fontFamily: "'DM Serif Display', serif" }}>
                        {phase.step}
                      </div>

                      <div className="relative z-10">
                        {/* Phase indicator rotated for style */}
                        <div className="flex items-center gap-3 mb-6">
                          <div className="w-12 h-12 rounded-xl bg-[#F5F1EA] flex items-center justify-center group-hover:bg-[#0C2340] transition-colors duration-300">
                            <Icon className="w-6 h-6 text-[#C8A24D] group-hover:text-[#E8D5A0] transition-colors duration-300" />
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-[#C8A24D] uppercase tracking-widest">{t("affiliates.process.phaseLabel")}</span>
                            <span className="text-lg font-bold text-[#0C2340]" style={{ fontFamily: "'DM Serif Display', serif" }}>{phase.step}</span>
                          </div>
                        </div>

                        <h3 className="text-xl text-[#0C2340] mb-3" style={{ fontFamily: "'DM Serif Display', serif" }}>
                          {phase.title}
                        </h3>
                        <p className="text-[#4A5568] text-sm leading-relaxed mb-6">
                          {phase.text}
                        </p>

                        {/* Detail tag */}
                        <div className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#C8A24D] bg-[#F5F1EA] px-3 py-1.5 rounded-full">
                          <ChevronRight className="w-3.5 h-3.5" />
                          {phase.detail}
                        </div>
                      </div>
                    </div>

                    {/* Connector arrow for desktop */}
                    {index < 2 && (
                      <div className="hidden md:flex absolute top-1/2 -right-4 lg:-right-4 z-20 -translate-y-1/2">
                        <div className="w-8 h-8 rounded-full bg-[#C8A24D] flex items-center justify-center shadow-lg shadow-[#C8A24D]/20">
                          <ChevronRight className="w-4 h-4 text-[#0C2340]" />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════
            8. TABLA DE INGRESOS
        ══════════════════════════════════════════════════ */}
        <section id="comisiones" className="section-reveal py-20 lg:py-28 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="text-[#C8A24D] text-sm font-semibold uppercase tracking-wider">{t("affiliates.income.badge")}</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl text-[#0C2340] mt-3" style={{ fontFamily: "'DM Serif Display', serif" }}>
                {t("affiliates.income.title")}
              </h2>
            </div>

            {/* Desktop table */}
            <div className="hidden sm:block overflow-hidden rounded-2xl border border-[#E8E4DC]">
              <table className="w-full">
                <thead>
                  <tr className="bg-[#0C2340] text-white text-left text-sm">
                    <th className="px-6 py-4 font-semibold">{t("affiliates.income.referralsLabel")}</th>
                    <th className="px-6 py-4 font-semibold">{t("affiliates.income.incomeLabel")}</th>
                  </tr>
                </thead>
                <tbody>
                  {INCOME_TABLE.map((row, i) => (
                    <tr key={i} className={`${i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"} border-t border-[#E8E4DC]/50`}>
                      <td className="px-6 py-4 text-[#4A5568] text-sm">{row.referrals}</td>
                      <td className="px-6 py-4 text-[#C8A24D] font-bold text-lg">{row.income}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile cards */}
            <div className="sm:hidden space-y-4">
              {INCOME_TABLE.map((row, i) => (
                <div key={i} className="bg-white rounded-2xl p-5 border border-[#E8E4DC]">
                  <p className="text-[#4A5568] text-xs mb-3">{row.referrals}</p>
                  <p className="text-[#C8A24D] font-bold text-2xl mb-1">{row.income}<span className="text-sm font-normal text-[#4A5568]">{t("affiliates.income.suffix")}</span></p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ══════════════════════════════════════════════════
            9. FAQ
        ══════════════════════════════════════════════════ */}
        <section id="faq" className="section-reveal py-20 lg:py-28 bg-[#FAFAF7]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="text-[#C8A24D] text-sm font-semibold uppercase tracking-wider">{t("affiliates.faq.badge")}</span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl text-[#0C2340] mt-3" style={{ fontFamily: "'DM Serif Display', serif" }}>
                {t("affiliates.faq.title")}
              </h2>
            </div>
            <div className="space-y-3">
              {FAQ_DATA.map((item) => (
                <FAQItem key={item.q} item={item} />
              ))}
            </div>
          </div>
        </section>



        {/* ══════════════════════════════════════════════════
            11. FINAL CTA BANNER
        ══════════════════════════════════════════════════ */}
        <section className="bg-gradient-to-r from-[#0C2340] to-[#162d4a] py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <span className="text-[#C8A24D] text-xs font-bold uppercase tracking-[0.2em] mb-4 block">{t("affiliates.finalCta.badge")}</span>
            <h2 className="text-3xl sm:text-4xl text-white mb-6" style={{ fontFamily: "'DM Serif Display', serif" }}>
              {t("affiliates.finalCta.title")}
            </h2>
            <p className="text-white/60 mb-10 max-w-xl mx-auto text-lg leading-relaxed">
              {t("affiliates.finalCta.subtitle")}
            </p>
            <button
              onClick={scrollToForm}
              className="inline-flex items-center gap-3 bg-[#C8A24D] text-[#0C2340] font-bold text-lg px-10 py-5 rounded-2xl hover:bg-[#B8922D] transition-all duration-300 shadow-xl shadow-[#C8A24D]/20 gold-shimmer"
            >
              {t("affiliates.finalCta.cta")}
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </section>
      </main>

      {/* ──── FOOTER ──── */}
      <footer className="bg-[#0A1929] text-white/40 text-sm text-center py-8 px-4">
        <p>© {new Date().getFullYear()} ResidenciaYA.es — {t("footer.copyright")}</p>
        <div className="flex flex-wrap justify-center gap-4 mt-3">
          <Link to={`/${i18n.language}/aviso-legal`} className="hover:text-[#C8A24D] transition-colors">{t("footer.link1")}</Link>
          <Link to={`/${i18n.language}/politica-privacidad`} className="hover:text-[#C8A24D] transition-colors">{t("footer.link2")}</Link>
          <Link to={`/${i18n.language}/politica-cookies`} className="hover:text-[#C8A24D] transition-colors">{t("footer.link3")}</Link>
          <Link to={`/${i18n.language}/aliados`} className="hover:text-[#C8A24D] transition-colors">{t("footer.link5")}</Link>
        </div>
      </footer>
    </div>
  );
}
